import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonService } from 'src/app/core/services/common.service';
import { RouteService } from 'src/app/core/services/route.service';
import { CloseChatBot, CloseChatPdf, GetChatMessage, GetChatPdf } from 'src/app/core/state/_actions/report.actions';
import { getChatError, getChatPdfError, getMessageData, getPdfMessage } from 'src/app/core/state/_selectors/report.selector';
import { SubSink } from 'subsink';

export interface Message{
  text: string,
  date: Date,
  reply: boolean,
  user: {
    name: string,
    avatar?: string
  }
}

@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class ChatBotComponent implements OnInit {
  @Input('isPdfChat') isPdfChat:boolean = false;
  @Input('fieldId') fieldId:any = '';
  @Input() isChatScreenOpen:boolean = false;
  @Output() onChatClose = new EventEmitter<any>();

  botAvatar = "assets/images/user.png";
  isChatFirstOpen:boolean = true;

  messages:Message[] = [];

  newMessage: string = '';

  reportId:any;
  
  tableKey:any;

  isLoading:boolean = false;
  
  recordId:any;

  subsink: SubSink = new SubSink();

  @ViewChild('chatContainer')scrollContainer!: ElementRef;


  constructor(
    private authService:AuthService,
    private store: Store, 
    private route: RouteService, 
    private render:Renderer2, 
    private el:ElementRef,
    private cd:ChangeDetectorRef,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.isChatScreenOpen = true;
    this.reportId = this.route.paramMap.reportId;
    this.recordId = this.route.paramMap.id;
    this.tableKey = this.route.getTableKey();
    this.subsink.add(
      this.store.pipe(
        select(this.isPdfChat ? getChatPdfError: getChatError),
      ).subscribe((error: any) => {
        if ((error || this.newMessage) && !this.isChatFirstOpen) {
          this.addErrorMessage(error);
        }
        this.newMessage = '';
        this.isChatFirstOpen = false;
        this.isLoading = false;
        this.cd.detectChanges();
      })
    );
    this.subsink.add(
      this.route.change().subscribe(()=>{
        if(window.location.href.includes('/auth/login')){
          this.isPdfChat ? this.closePdfChatSession() : this.closeChatSession();
        }
      })
    );
  }

  get tableId(){
    return this.route.getTableKey();
  }

  get isOpenGovTheme() {
    return !this.commonService.isIgnatiusThemeApplied;
  }

  sendMessage = ()=> {
    const trimmedMessage = this.newMessage.trim();
    if (trimmedMessage === '') {
      return;
    }

    const takeValue = this.messages.find((message)=> message.text === trimmedMessage) ? 1 : 2 ;

    this.addMessageToChat(trimmedMessage, trimmedMessage, true);
    this.messageDataSubscriber(takeValue,trimmedMessage);
      this.scrollToBottom();
  }

  messageDataSubscriber(takeValue:any,message:any){
    this.subsink.add(
      this.isPdfChat ?
      this.store.pipe(
        select(getPdfMessage),
        take(takeValue),
        map(messageData => messageData[this.tableId]?.[this.reportId]?.[this.recordId]?.[this.fieldId] ?? null)
      ).subscribe((data) => {
        this.addDispatchRequest(data,message);
      }) : 
      this.store.pipe(
        select(getMessageData),
        take(takeValue),
        map(messageData => messageData[this.tableId]?.[this.reportId] ?? null)
      ).subscribe((data) => {
        this.addDispatchRequest(data,message);
      })
    );
  }

  addDispatchRequest(data:any,message:any){
    if (data && data[message]) {
      this.addMessageToChat(message, data[message]);
      this.isLoading = false;
      this.scrollToBottom();
    } else {
      this.dispatchChatMessage(message);
    }
  }

  supportClicked(){
    if(this.isChatScreenOpen){
      this.messages = [];
    }
    this.isChatScreenOpen = !this.isChatScreenOpen;
    this.isLoading = false;
    if(!this.isChatScreenOpen){
      this.closeChatSession();
    }
  }

  closeChatScreen(){
    this.isChatScreenOpen = false;
    this.messages = [];
    this.isLoading = false;
    this.onChatClose.emit();
    if(!this.isChatScreenOpen){
      this.isPdfChat ? this.closePdfChatSession() : this.closeChatSession();
    }
  }

  addErrorMessage(error: any) {
    const errorMessage: Message = {
      text: error && error.status ? error.message.includes('Http') ? 'Please log in again to continue' : error.message : 'Sorry, I am unable to answer this question right now.',
      date: new Date(),
      reply: false,
      user: {
        name: 'Ignatius',
        avatar: this.botAvatar
      }
    };
    this.messages.push(errorMessage);
  }

  dispatchChatMessage(question: string): void {
    if(!this.isChatScreenOpen) return;
    let messagePayload:any = {
      table_id: this.tableId,
      report_id: this.reportId,
      bearer_token: this.authService.getMLAccessToken(),
      question: question,
      user_id: this.authService.userProfile.UserName
    };
    if(this.isPdfChat){
      messagePayload['record_id'] = this.recordId;
      messagePayload['field_id'] = this.fieldId;
      this.store.dispatch(new GetChatPdf(messagePayload, this.tableId, this.reportId,this.recordId,this.fieldId));
    }else {
      this.store.dispatch(new GetChatMessage(messagePayload, this.tableId, this.reportId));
    }
    this.isLoading = true;
  }

  addMessageToChat(originalMessage: string, responseMessage: any, isUser: boolean = false) {
    let responseMssg = '';
    if(!isUser){
      responseMessage.forEach((message:any)=>{
        if (message.startsWith("assistant:")) {
          message = message.substring("assistant:".length).trim();
        }
        responseMssg += message;
      })
    }
    const message: Message = {
      text: isUser ? responseMessage : responseMssg,
      date: new Date(),
      reply: !isUser,
      user: {
        name: isUser ? this.authService.userProfile.UserName : 'Ignatius',
        avatar: isUser ? undefined : this.botAvatar
      }
    };
    this.messages.push(message);
    if (isUser) {
      this.newMessage = '';
    }
  }

  scrollToBottom() {
      this.cd.detectChanges();
      const scrollContainer = this.el.nativeElement.querySelector('.chat-message-list');
      this.render.setStyle(scrollContainer,'scrollTop',this.scrollContainer.nativeElement.scrollHeight)
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    }

  closeChatSession(){
    const chatSessionPayload = {
      table_id: this.tableId || this.tableKey,
      report_id: this.reportId,
      user_id:this.authService.userProfile.UserName
    }
    this.store.dispatch(new CloseChatBot(chatSessionPayload,this.tableId,this.reportId));
  }

  closePdfChatSession(){
    const chatSessionPayload = {
      table_id: this.tableId || this.tableKey,
      report_id: this.reportId,
      record_id: this.recordId,
      field_id: this.fieldId,
      user_id:this.authService.userProfile.UserName
    }
    this.store.dispatch(new CloseChatPdf(chatSessionPayload,this.tableId,this.reportId,this.recordId,this.fieldId));
  }
  ngOnDestroy() {
    if(this.isChatScreenOpen){
      this.isPdfChat ? this.closePdfChatSession() : this.closeChatSession();
    }
    this.subsink.unsubscribe();
  }

  setInputValue = (event: any) => {
    if (event.target.value.length < 3 && event.target.value.length > 0) return;
    this.newMessage = event.target.value;
  };

}
